import type { OptionsLibraryEntry } from "./optionsLibrary";

const LEGACY_UFR_TYPO_TO_CATALOG: Record<string, string> = {
  "3360": "3470",
  "3377": "3477",
};

const UFR_PATTERN = /^(\d+)UFR(\d+P\d+)$/i;

/** Legacy Sunlabs article numbers and typos → catalog lookup keys. */
export function creeperWheelLookupKeys(legacyName: string): string[] {
  const key = legacyName.replaceAll(/\s/g, "");
  const keys = new Set<string>([key]);

  const ufrMatch = UFR_PATTERN.exec(key);
  const articleNumber = ufrMatch?.[1];
  const ufrSuffix = ufrMatch?.[2];
  if (articleNumber && ufrSuffix) {
    keys.add(`ROL${articleNumber}`);
    const corrected = LEGACY_UFR_TYPO_TO_CATALOG[articleNumber];
    if (corrected) {
      keys.add(`${corrected}UFR${ufrSuffix}`);
      keys.add(`ROL${corrected}`);
    }
  }

  return [...keys];
}

/** Single-caster creeper catalog row: meshes.default present, no bundle rollerA slot. */
export function isSingleWheelCreeperEntry(entry: OptionsLibraryEntry): boolean {
  if (entry.kind !== "RollerType" || entry.rollerType !== 4) {
    return false;
  }
  const meshes = entry.meshes;
  if (!meshes?.default || meshes.rollerA) {
    return false;
  }
  return true;
}

function matchesCreeperLookupKey(
  entry: OptionsLibraryEntry,
  normalized: string,
): boolean {
  const canonical = typeof entry.name === "string" ? entry.name.trim() : "";
  if (!canonical) {
    return false;
  }
  if (canonical.replaceAll(/\s/g, "") === normalized) {
    return true;
  }
  return (
    entry.aliases?.some(
      (alias) =>
        typeof alias === "string" && alias.replaceAll(/\s/g, "") === normalized,
    ) ?? false
  );
}

/** Resolves legacy creeper caster names (e.g. 3477UFR100P62) to catalog row. */
export function findSingleCreeperWheelEntryInLibrary(
  legacyName: string | undefined,
  library: ReadonlyArray<OptionsLibraryEntry>,
): OptionsLibraryEntry | undefined {
  if (!legacyName?.trim()) {
    return undefined;
  }

  for (const lookupKey of creeperWheelLookupKeys(legacyName)) {
    const normalized = lookupKey.replaceAll(/\s/g, "");
    for (const entry of library) {
      if (!isSingleWheelCreeperEntry(entry)) {
        continue;
      }
      if (matchesCreeperLookupKey(entry, normalized)) {
        return entry;
      }
    }
  }

  return undefined;
}

/** Resolves legacy creeper caster names (e.g. 3477UFR100P62) to catalog name (ROL3477). */
export function findSingleCreeperWheelInLibrary(
  legacyName: string | undefined,
  library: ReadonlyArray<OptionsLibraryEntry>,
): string | undefined {
  const entry = findSingleCreeperWheelEntryInLibrary(legacyName, library);
  const canonical = typeof entry?.name === "string" ? entry.name.trim() : "";
  return canonical || undefined;
}
